import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, MonitorSmartphone } from "lucide-react";
import { usePwaInstall } from "@/lib/pwa";

const DISMISS_KEY = "imperium-install-dismissed";

// Discreet fixed banner inviting visitors to install the IMPERIUM app.
// Appears only when installable, and stays dismissed once closed.
export const InstallBanner = () => {
  const { canInstall, ios, promptInstall } = usePwaInstall();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });
  const [ready, setReady] = useState(false);

  // Small delay so it slides in after the page settles (less intrusive).
  useEffect(() => {
    const id = setTimeout(() => setReady(true), 1500);
    return () => clearTimeout(id);
  }, []);

  const close = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
  };

  const show = ready && canInstall && !dismissed;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          data-testid="install-banner"
          className="fixed bottom-4 inset-x-4 md:inset-x-auto md:right-6 md:bottom-6 md:max-w-md z-[70]"
        >
          <div className="glass border border-aurora-cyan/30 rounded-lg p-4 shadow-2xl flex items-center gap-4">
            <div className="w-11 h-11 shrink-0 rounded-md bg-aurora-cyan/15 border border-aurora-cyan/30 grid place-items-center text-aurora-cyan">
              <MonitorSmartphone className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display font-bold uppercase tracking-wide text-sm text-white">Installez l'app IMPERIUM</div>
              <p className="text-xs text-zinc-400 leading-snug mt-0.5">
                {ios
                  ? "Ajoutez-la à votre écran d'accueil pour un accès direct."
                  : "Accès direct depuis votre bureau, comme une vraie application."}
              </p>
            </div>
            <button
              onClick={promptInstall}
              data-testid="install-banner-btn"
              className="shrink-0 flex items-center gap-1.5 px-3.5 h-10 rounded-sm bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider text-xs hover:brightness-110 transition-all"
            >
              <Download className="w-4 h-4" /> Installer
            </button>
            <button
              onClick={close}
              data-testid="install-banner-close"
              aria-label="Fermer"
              className="shrink-0 w-8 h-8 grid place-items-center rounded-sm text-zinc-500 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
